import React, { useEffect, useRef, useState } from 'react';
import { usePresentationStore } from '../../stores/presentationStore';
import { useUiStore } from '../../stores/uiStore';
import { drawSlideThumb, slideThumbKey } from '../../editor/slideThumb.js';
import { nextVisibleSlideIndex, visibleSlideIndices } from '../../editor/slideVisibility.js';
import { hydrateSlides } from '../../editor/mediaStore.js';
import { hydrateMissingFormulas } from '../../editor/slideThumb.js';
import { versionAttr } from '../../editor/versions.js';
import { postPresenter, subscribePresenter } from '../../editor/presenterChannel.js';

function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const ss = String(s % 60).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  if (h > 0) return `${h}:${mm}:${ss}`;
  return `${mm}:${ss}`;
}

function MiniThumb({ slide, slideIndex, canvasW, canvasH, w, h, label, black }) {
  const ref = useRef(null);
  const [asyncTick, setAsyncTick] = useState(0);
  const thumbKey = slide
    ? slideThumbKey(slide, slideIndex, `${canvasW}x${canvasH}`)
    : `empty-${slideIndex}`;
  useEffect(() => {
    setAsyncTick(0);
  }, [thumbKey]);
  useEffect(() => {
    const c = ref.current;
    if (!c || !slide) return;
    drawSlideThumb(c, slide, canvasW, canvasH, {
      onAsyncReady: () => setAsyncTick((t) => t + 1),
    });
  }, [slide, canvasW, canvasH, thumbKey, slideIndex, asyncTick, w, h]);
  return (
    <div className="presenter-thumb">
      <div className="presenter-thumb-label">{label}</div>
      <div className="presenter-thumb-frame">
        {slide ? (
          <canvas ref={ref} width={w} height={h} aria-hidden="true" />
        ) : (
          <div className="presenter-thumb-empty">—</div>
        )}
        {black ? <div className="presenter-thumb-black">{'●'}</div> : null}
      </div>
    </div>
  );
}

export default function PresenterView() {
  const lang = useUiStore((s) => s.lang);
  const ru = lang !== 'en';
  const slides = usePresentationStore((s) => s.slides);
  const cur = usePresentationStore((s) => s.cur);
  const canvasW = usePresentationStore((s) => s.canvasW);
  const canvasH = usePresentationStore((s) => s.canvasH);
  const deckTitle = usePresentationStore((s) => s.title);
  const [live, setLive] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [timerOrigin, setTimerOrigin] = useState(0);

  useEffect(() => {
    document.documentElement.classList.add('presenter-mode');
    document.title = ru ? 'Докладчик' : 'Presenter';
    return () => document.documentElement.classList.remove('presenter-mode');
  }, [ru]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await usePresentationStore.getState().hydrateFromIdb();
      } catch (e) {}
      if (cancelled) return;
      try {
        const list = usePresentationStore.getState().slides;
        const n = await hydrateSlides(list);
        let formulas = 0;
        try {
          formulas = await hydrateMissingFormulas(list);
        } catch (e) {}
        if (n > 0 || formulas > 0) {
          usePresentationStore.setState({ slides: [...list] });
        }
      } catch (e) {}
    })();
    postPresenter({ t: 'hello' });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return subscribePresenter((msg) => {
      if (!msg || typeof msg !== 'object') return;
      if (msg.t === 'state') setLive(msg);
      if (msg.t === 'bye') {
        setLive(null);
        setTimerOrigin(0);
      }
    });
  }, []);

  const idx = live?.live ? live.idx | 0 : cur | 0;
  const shuffle = !!(live && live.shuffle);
  const nextIdx = live?.live
    ? live.nextIdx | 0
    : nextVisibleSlideIndex(slides, idx, 1);
  const vis = visibleSlideIndices(slides);
  const visPos = live?.live ? live.visPos | 0 : vis.indexOf(idx) + 1;
  const visN = live?.live ? live.visN | 0 : vis.length;
  const title = (live?.title || deckTitle || '').trim();
  const black = !!(live && live.black);
  const startedAt = Number(live?.startedAt) || 0;

  useEffect(() => {
    if (startedAt && !timerOrigin) setTimerOrigin(startedAt);
  }, [startedAt, timerOrigin]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const origin = timerOrigin || startedAt;
  const elapsed = origin ? now - origin : 0;

  const ar = canvasW > 0 ? canvasH / canvasW : 9 / 16;
  const curW = 720;
  const curH = Math.max(200, Math.round(curW * ar));
  const nextW = 360;
  const nextH = Math.max(120, Math.round(nextW * ar));

  const currentSlide = slides[idx] || null;
  const resolvedNext =
    shuffle || nextIdx < 0 || nextIdx === idx ? null : slides[nextIdx] || null;

  const send = (cmd, extra) => postPresenter({ t: 'cmd', cmd, ...extra });

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        window.close();
        return;
      }
      if (e.code === 'KeyB') {
        e.preventDefault();
        send('black');
        return;
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        send('next');
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        send('prev');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const status = live?.live
    ? ru
      ? 'Показ идёт'
      : 'Live'
    : ru
      ? 'Ожидание показа (F5 в редакторе)'
      : 'Waiting for slideshow (F5 in editor)';

  const nextCaption = shuffle
    ? ru
      ? 'Следующий — случайно'
      : 'Next — shuffled'
    : resolvedNext
      ? ru
        ? `Следующий · ${nextIdx + 1}`
        : `Next · ${nextIdx + 1}`
      : ru
        ? 'Конец'
        : 'End';

  return (
    <div className="presenter-root" {...versionAttr('PresenterView')}>
      <header className="presenter-bar">
        <div className="presenter-bar-title">{title || (ru ? 'Презентация' : 'Presentation')}</div>
        <div className={`presenter-status${live?.live ? ' is-live' : ''}`}>{status}</div>
        <div className="presenter-timer" title={ru ? 'Сброс таймера' : 'Reset timer'}>
          <button type="button" onClick={() => setTimerOrigin(Date.now())}>
            {formatElapsed(elapsed)}
          </button>
        </div>
        <div className="presenter-count">
          {visPos || idx + 1} / {visN || slides.length}
        </div>
      </header>
      <div className="presenter-stage">
        <MiniThumb
          slide={currentSlide}
          slideIndex={idx}
          canvasW={canvasW}
          canvasH={canvasH}
          w={curW}
          h={curH}
          label={ru ? `Сейчас · ${idx + 1}` : `Now · ${idx + 1}`}
          black={black}
        />
        <MiniThumb
          slide={resolvedNext}
          slideIndex={resolvedNext ? nextIdx : -1}
          canvasW={canvasW}
          canvasH={canvasH}
          w={nextW}
          h={nextH}
          label={nextCaption}
          black={false}
        />
      </div>
      <footer className="presenter-actions">
        <button type="button" onClick={() => send('prev')} disabled={!live?.live}>
          {ru ? '← Назад' : '← Prev'}
        </button>
        <button type="button" className="primary" onClick={() => send('next')} disabled={!live?.live}>
          {ru ? 'Далее →' : 'Next →'}
        </button>
        <button type="button" onClick={() => send('black')} disabled={!live?.live}>
          {black ? (ru ? 'Вернуть слайд (B)' : 'Show slide (B)') : ru ? 'Чёрный экран (B)' : 'Black (B)'}
        </button>
        <button type="button" onClick={() => send('exit')} disabled={!live?.live}>
          {ru ? 'Закончить показ' : 'End show'}
        </button>
      </footer>
    </div>
  );
}
